import { useQueryClient } from "@tanstack/react-query";
import { validateAndConvertReqParamToInt } from "_services/utils/validateAndConvertReqParamToNum";
import { LorryListTypes } from "../../custom-types/lorry";
import { getAuthIdParam } from "../../methods/getAuthIdParam";
import API from "../const";
import { InfiniteQueryData, QueryOptions } from "../type";
import { getAuthHeader } from "../utils/getAuthHeader";
import { getInfiniteQueryCurrentDataLength } from "../utils/getInfiniteQueryCurrentDataLength";
import { QueryNames } from "./queryNames";
import { API_REQUEST_TYPE } from "../../constants/api";
import { useVahakInfiniteQuery } from "../../hooks/useVahakQueryClient";

interface LorrySearchQueryParams {
    page?: number;
    c_i?: string | number;
    lorry_type_id?: number;
    city_id?: number;
    state_id?: number;
    destination_city_id?: number;
    destination_state_id?: number;
    capacity_string?: string;
    l?: number;
    vehicle_verified_only?: boolean;
}
export interface LorrySearchParams {
    stateId?: number;
    cityId?: number;
    destinationCityId?: number;
    routeId?: number;
    currentCityId?: number;
    page?: number;
    lorry_type?: number;
    capacity?: { min?: number; max?: number };
    company_id?: number | string;
    lorryIdsToExclude?: number[];
    pageSize?: number;
    origin?: string;
    isVerifiedVehicle?: boolean;
    isAdmin: boolean;
}

type GetLorriesForCurrentUserResponse = InfiniteQueryData<LorryListTypes>;

interface GetLorriesForCurrentUserParams {
    companyId?: number | string;
    request_type?: number;
    type?: string[];
    status?: string[];
    created_at_string?: string;
    id?: number[];
    preloads?: string[];
}

export function useGetLorriesForCurrentUserQuery(
    {
        companyId,
        type,
        status,
        request_type = API_REQUEST_TYPE.ELASTIC_SEARCH,
        created_at_string,
        id,
        preloads = []
    }: GetLorriesForCurrentUserParams,
    { enabled, refetchInterval, onSuccess, onError }: QueryOptions
) {
    const queryClient = useQueryClient();
    const { data, fetchNextPage, hasNextPage, isFetching, isLoading, refetch } = useVahakInfiniteQuery<
        GetLorriesForCurrentUserResponse,
        Error
    >(
        [QueryNames.lorrySearch, companyId, type, status, request_type, created_at_string, id],
        async ({ pageParam = 1 }) => {
            const validatedCompanyId = validateAndConvertReqParamToInt("companyId", companyId);
            if (!validatedCompanyId) {
                return { results: [], next: undefined };
            }
            const response = await fetch(API.ALL_LORRIES + getAuthIdParam(), {
                body: JSON.stringify({
                    company_id: validatedCompanyId,
                    page_number: pageParam,
                    request_type,
                    status,
                    created_at_string,
                    type,
                    id,
                    preload: ["bid_infos", "company_detail", "lorry_details", ...preloads]
                }),
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...getAuthHeader()
                }
            });
            if (response.status === 401) {
                // this will cause the AuthVerifyComp to re-render and when token verification fails, logout the app
                queryClient.invalidateQueries(QueryNames.validateToken);
            }
            if (!response.ok) {
                throw new Error("Problem fetching data");
            }
            const dataFromServer = await response.json();

            const data: GetLorriesForCurrentUserResponse = {
                results: dataFromServer.data.lorry_cards,
                totalCount: dataFromServer.data.total_records,
                next: pageParam < dataFromServer.data.total_pages ? pageParam + 1 : undefined
            };
            return data;
        },
        {
            getNextPageParam: (lastPage) => lastPage.next,
            refetchInterval: refetchInterval || false,
            enabled,
            onSuccess: onSuccess ?? (() => {}),
            onError: onError ?? (() => {})
        }
    );
    return {
        data,
        fetchNextPage,
        hasNextPage,
        currentDataLength: getInfiniteQueryCurrentDataLength(data),
        isFetching,
        isLoading,
        refetch
    };
}
